import { useRef } from "react";
import { motion, useInView } from "framer-motion";

const STEPS = [
  {
    n: "01",
    title: "Consultation",
    body: "We start with a conversation — your space, your style, your storage needs. In-home or virtual, we measure twice so we can cut once.",
  },
  {
    n: "02",
    title: "Design & Quote",
    body: "You receive detailed drawings, wood and finish selections, and a transparent, itemized quote. No surprises, no filler.",
  },
  {
    n: "03",
    title: "Craft",
    body: "Your cabinets are built by hand in our shop using premium hardwoods and time-honored joinery. Every panel is inspected before it leaves.",
  },
  {
    n: "04",
    title: "Install",
    body: "We deliver and install with care, protecting your home throughout. The result: cabinetry that fits like it grew there.",
  },
];

function Chapter({ step, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const flip = index % 2 === 1;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`process-step-${step.n}`}
      className={`grid grid-cols-12 gap-4 items-start py-12 border-t border-charcoal/15 ${
        flip ? "lg:pl-[16%]" : "lg:pr-[16%]"
      }`}
    >
      <div className={`col-span-12 lg:col-span-4 ${flip ? "lg:order-2 lg:text-right" : ""}`}>
        <span className="font-serif text-terracotta text-7xl md:text-8xl leading-none">{step.n}</span>
      </div>
      <div className={`col-span-12 lg:col-span-8 ${flip ? "lg:order-1" : ""}`}>
        <h3 className="font-serif text-3xl md:text-4xl text-charcoal mb-4">{step.title}</h3>
        <p className="font-sans text-charcoal/70 leading-relaxed max-w-md">{step.body}</p>
      </div>
    </motion.div>
  );
}

export function HowItWorks() {
  return (
    <section id="process" data-testid="process-section" className="bg-cream py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="mb-16 max-w-2xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-12 bg-terracotta" />
            <span className="font-sans uppercase tracking-[0.3em] text-xs text-charcoal/60">The Process</span>
          </div>
          <h2 className="font-serif text-charcoal text-5xl md:text-6xl tracking-tight leading-[0.95]">
            Four steps from <span className="italic text-terracotta">idea</span> to installed.
          </h2>
        </div>
        <div>
          {STEPS.map((s, i) => (
            <Chapter key={s.n} step={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
